// /bookings/:id — Booking confirmation + actions (G3.7)
// Shows the booking summary (confirmation card), item list, payment state,
// reminder timeline, and role-gated action rail for deposit/balance/reminder/cancel.
// RESTAURANT vertical · MANAGER+ view · CASHIER+ mutate · MANAGER+ reminder ·
// ENABLE_FESTIVE_MODULE.

import { getSubunitMultiplier } from '@bbpos/utils';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  TextArea,
  Modal,
  ConfirmDialog,
  Skeleton,
  ErrorBoundary,
  EmptyState,
  useToast,
} from '@bbpos/ui';
import {
  useBooking,
  useBookingReminders,
  useAddDeposit,
  usePayBalance,
  useSendReminder,
  useCancelBooking,
  useUpdateBooking,
  STATUS_BADGE,
  formatDeliveryTime,
  SLOT_HOURS,
  PAYMENT_METHOD_OPTIONS,
  REMINDER_CHANNELS,
  REMINDER_TYPE_LABEL,
  formatBookingDate,
  formatBookingDateTime,
  depositPercent,
  daysUntil,
} from '@/features/bookings';
import type {
  Booking,
  BookingPaymentMethod,
  ReminderChannel,
} from '@/features/bookings';
import { useAuthStore } from '@/stores/use-auth-store';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { ROLE_LEVELS } from '@/types/user';
import { formatPaise, formatPaiseCompact } from '@/utils/currency';
import { cn, GLASS } from '@/constants/design-tokens';
import {
  ArrowLeftIcon,
  BanIcon,
  BellIcon,
  CalendarIcon,
  CheckIcon,
  ClockIcon,
  CurrencyIcon,
  EditIcon,
  PackageIcon,
  PhoneIcon,
  SendIcon,
  SparkleIcon,
  UsersIcon,
} from './icons';

type PaymentMode = 'DEPOSIT' | 'BALANCE';

function PaymentModal({
  open,
  mode,
  maxAmount,
  initialAmount,
  defaultMethod,
  onClose,
  onSubmit,
  loading,
}: {
  open: boolean;
  mode: PaymentMode;
  maxAmount: number;
  initialAmount?: number;
  defaultMethod: BookingPaymentMethod;
  onClose: () => void;
  onSubmit: (amount: number, method: BookingPaymentMethod) => void;
  loading: boolean;
}) {
  const currency = useTenantCurrency();
  const [amount, setAmount] = useState<string>('');
  const [method, setMethod] = useState<BookingPaymentMethod>(defaultMethod);

  useEffect(() => {
    if (open) {
      setAmount(initialAmount ? String(initialAmount / getSubunitMultiplier(currency)) : '');
      setMethod(defaultMethod);
    }
  }, [open, initialAmount, defaultMethod]);

  const paise = Math.round(Number(amount || 0) * getSubunitMultiplier(currency));
  const invalid =
    !paise ||
    paise <= 0 ||
    paise > maxAmount + (mode === 'BALANCE' ? 0 : 0) ||
    (mode === 'BALANCE' && paise !== maxAmount);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'DEPOSIT' ? 'Add deposit' : 'Pay balance'}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => onSubmit(paise, method)}
            disabled={invalid || loading}
          >
            {loading ? 'Processing…' : mode === 'DEPOSIT' ? 'Apply deposit' : 'Settle balance'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="text-sm text-neutral-600 dark:text-neutral-300">
          {mode === 'BALANCE' ? (
            <>
              Remaining balance is{' '}
              <span className="font-mono tabular-nums font-semibold text-neutral-900 dark:text-neutral-100">
                {formatPaise(maxAmount, currency)}
              </span>
              . Paying in full will transition this booking to <strong>DELIVERED</strong>.
            </>
          ) : (
            <>
              Maximum deposit is{' '}
              <span className="font-mono tabular-nums font-semibold text-neutral-900 dark:text-neutral-100">
                {formatPaise(maxAmount, currency)}
              </span>{' '}
              (remaining balance).
            </>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
            Amount ({getCurrencySymbol(currency)})
          </label>
          <Input
            type="number"
            placeholder="0.00"
            value={amount}
            onChange={(v) => setAmount(typeof v === 'string' ? v : String(v))}
            disabled={mode === 'BALANCE'}
          />
          {mode === 'DEPOSIT' && paise > maxAmount && (
            <p className="mt-1 text-xs text-danger-600">Exceeds remaining balance</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
            Payment method
          </label>
          <div className="grid grid-cols-3 gap-2">
            {PAYMENT_METHOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setMethod(opt.value)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-sm font-medium transition-all duration-150',
                  method === opt.value
                    ? 'bg-brand-600 text-white border-brand-600 shadow-sm'
                    : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 border-neutral-200 dark:border-neutral-700 hover:border-brand-400',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function ReminderModal({
  open,
  onClose,
  onSubmit,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (channel: ReminderChannel, message?: string) => void;
  loading: boolean;
}) {
  const [channel, setChannel] = useState<ReminderChannel>('WHATSAPP');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (open) {
      setChannel('WHATSAPP');
      setMessage('');
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send manual reminder"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => onSubmit(channel, message.trim() || undefined)}
            disabled={loading}
          >
            {loading ? 'Queuing…' : 'Send reminder'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
            Channel
          </label>
          <div className="grid grid-cols-2 gap-2">
            {REMINDER_CHANNELS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setChannel(opt.value)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-sm font-medium transition-all duration-150',
                  channel === opt.value
                    ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                    : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 border-neutral-200 dark:border-neutral-700 hover:border-violet-400',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
            Message <span className="text-neutral-400 font-normal">(optional)</span>
          </label>
          <TextArea
            value={message}
            onChange={(v) => setMessage(typeof v === 'string' ? v : String(v))}
            placeholder="Leave blank to send the default reminder template."
            rows={4}
          />
          <p className="mt-1 text-xs text-neutral-500">
            Max 500 characters. Default template includes booking number, delivery date, and location.
          </p>
        </div>
      </div>
    </Modal>
  );
}

function EditModal({
  open,
  onClose,
  booking,
  onSubmit,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  booking: Booking;
  onSubmit: (payload: { delivery_date?: string; delivery_slot?: string; notes?: string }) => void;
  loading: boolean;
}) {
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [slot, setSlot] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  useEffect(() => {
    if (open) {
      setDeliveryDate(booking.delivery_date?.slice(0, 10) ?? '');
      setSlot(booking.delivery_slot ?? '');
      setNotes(booking.notes ?? '');
    }
  }, [open, booking]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit booking"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              onSubmit({
                delivery_date: deliveryDate || undefined,
                delivery_slot: slot || undefined,
                notes: notes,
              })
            }
            disabled={loading}
          >
            {loading ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-neutral-500">
          Item quantities are edited from the POS. Here you can reschedule the delivery window and update notes.
        </p>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
            Delivery date
          </label>
          <Input
            type="date"
            value={deliveryDate}
            onChange={(v) => setDeliveryDate(typeof v === 'string' ? v : String(v))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
            Pickup Time
          </label>
          <input
            type="time"
            value={/^\d{1,2}:\d{2}$/.test(slot) ? slot : ''}
            onChange={(e) => setSlot(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-600 text-sm text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
            Notes
          </label>
          <TextArea
            value={notes}
            onChange={(v) => setNotes(typeof v === 'string' ? v : String(v))}
            rows={3}
            placeholder="Special requests, delivery instructions…"
          />
        </div>
      </div>
    </Modal>
  );
}

function CancelResultModal({
  open,
  onClose,
  result,
}: {
  open: boolean;
  onClose: () => void;
  result: { cancellation_charge: number; refund_amount: number } | null;
}) {
  const currency = useTenantCurrency();
  if (!result) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Booking cancelled"
      footer={
        <div className="flex justify-end">
          <Button variant="primary" onClick={onClose}>
            Got it
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          The booking was cancelled successfully. Refund details are shown below for reconciliation.
        </p>
        <div className="rounded-xl bg-gradient-to-r from-danger-50 to-warning-50 dark:from-danger-900/20 dark:to-warning-900/20 p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-neutral-500">Cancellation charge</span>
            <span className="font-mono tabular-nums font-semibold text-danger-600">
              {formatPaise(result.cancellation_charge, currency)}
            </span>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs uppercase tracking-wide text-neutral-500">Refund amount</span>
            <span className="font-mono tabular-nums font-semibold text-success-600">
              {formatPaise(result.refund_amount, currency)}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Main content ───────────────────────────────────────────────────────────

function BookingsDetailContent() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canMutate = roleLevel >= ROLE_LEVELS.CASHIER;
  const canRemind = roleLevel >= ROLE_LEVELS.MANAGER;

  // Single GET /bookings/:id call — returns the booking with items, festival
  // name, and location name pre-joined. RLS scopes by tenant on the server.
  const { data: booking, isLoading, error, refetch } = useBooking(id);

  useEffect(() => {
    if (error) toast({ title: (error as Error).message, variant: 'error' });
  }, [error, toast]);

  const { data: remindersData } = useBookingReminders(id);
  const reminders = remindersData?.reminders ?? [];

  const addDeposit = useAddDeposit();
  const payBalance = usePayBalance();
  const sendReminder = useSendReminder();
  const cancelBooking = useCancelBooking();
  const updateBooking = useUpdateBooking();

  // Modal state
  const [paymentModal, setPaymentModal] = useState<PaymentMode | null>(null);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [cancelResult, setCancelResult] = useState<{
    cancellation_charge: number;
    refund_amount: number;
  } | null>(null);

  const festivalName = booking?.festival_name ?? '';

  // ── Loading skeleton ────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="grid gap-4 lg:grid-cols-[2fr,1fr]">
        <Skeleton variant="rect" height={520} className="rounded-2xl" />
        <Skeleton variant="rect" height={320} className="rounded-2xl" />
      </div>
    );
  }

  if (!booking) {
    return (
      <EmptyState
        variant="page"
        icon={<CalendarIcon className="h-full w-full" />}
        title="Booking not found"
        description="This booking may have been cancelled or belongs to a festival you don't have access to."
        action={{ label: 'Back to bookings', onClick: () => navigate('/bookings') }}
      />
    );
  }

  const b = booking;
  const statusMeta = STATUS_BADGE[b.status] ?? STATUS_BADGE.PENDING;
  const pct = depositPercent(b.advance_amount, b.total_amount);
  const balanceDue = b.balance_due;
  const countdown = daysUntil(b.delivery_date);
  const overdue = countdown !== null && countdown < 0 && b.status !== 'DELIVERED' && b.status !== 'CANCELLED';
  const canEdit = canMutate && b.status === 'CONFIRMED';
  const canCancel = canMutate && (b.status === 'CONFIRMED' || b.status === 'PENDING');
  const canDeposit = canMutate && balanceDue > 0 && (b.status === 'CONFIRMED' || b.status === 'READY');
  const canPayBalance = canDeposit;

  function handleDepositSubmit(amount: number, method: BookingPaymentMethod) {
    addDeposit.mutate(
      { id: b.id, payload: { amount, payment_method: method } },
      {
        onSuccess: () => {
          toast({ title: `Deposit of ${formatPaise(amount, currency)} applied`, variant: 'success' });
          setPaymentModal(null);
          refetch();
        },
        onError: (err) => toast({ title: err.message, variant: 'error' }),
      },
    );
  }

  function handleBalanceSubmit(amount: number, method: BookingPaymentMethod) {
    payBalance.mutate(
      { id: b.id, payload: { amount, payment_method: method } },
      {
        onSuccess: () => {
          toast({ title: 'Balance settled — booking delivered', variant: 'success' });
          setPaymentModal(null);
          refetch();
        },
        onError: (err) => toast({ title: err.message, variant: 'error' }),
      },
    );
  }

  function handleReminderSubmit(channel: ReminderChannel, message?: string) {
    sendReminder.mutate(
      { id: b.id, payload: { channel, message } },
      {
        onSuccess: () => {
          toast({ title: `${channel} reminder queued`, variant: 'success' });
          setReminderOpen(false);
        },
        onError: (err) => toast({ title: err.message, variant: 'error' }),
      },
    );
  }

  function handleEditSubmit(payload: {
    delivery_date?: string;
    delivery_slot?: string;
    notes?: string;
  }) {
    updateBooking.mutate(
      { id: b.id, payload },
      {
        onSuccess: () => {
          toast({ title: 'Booking updated', variant: 'success' });
          setEditOpen(false);
          refetch();
        },
        onError: (err) => toast({ title: err.message, variant: 'error' }),
      },
    );
  }

  function handleCancelConfirm() {
    cancelBooking.mutate(
      { id: b.id },
      {
        onSuccess: (result) => {
          setConfirmCancelOpen(false);
          setCancelResult({
            cancellation_charge: result.cancellation_charge,
            refund_amount: result.refund_amount,
          });
          refetch();
        },
        onError: (err) => {
          toast({ title: err.message, variant: 'error' });
          setConfirmCancelOpen(false);
        },
      },
    );
  }

  const whatsappHref = `https://wa.me/${b.customer_phone.replace(/[^0-9]/g, '')}`;

  return (
    <>
      {/* Back link + header strip */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 animate-fade-in">
        <button
          type="button"
          onClick={() => navigate('/bookings')}
          className="inline-flex items-center gap-1.5 text-sm text-neutral-600 dark:text-neutral-300 hover:text-brand-600 dark:hover:text-brand-300 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to bookings
        </button>
        {overdue && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-200">
            <ClockIcon className="h-3.5 w-3.5" />
            Delivery overdue
          </span>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-[2fr,1fr] items-start">
        {/* ── Left: Confirmation card ───────────────────────────────────── */}
        <div className={cn(GLASS, 'p-5 md:p-6 animate-slide-up')}>
          {/* Header block */}
          <div className="flex flex-wrap items-start justify-between gap-3 pb-4 border-b border-neutral-100 dark:border-neutral-800">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold',
                    statusMeta.tint,
                  )}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  {statusMeta.label}
                </span>
                <span className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                  {b.source === 'SELF_QR' ? 'Self-booking via QR' : 'Staff-created'}
                </span>
              </div>
              <p className="mt-2 text-xs text-neutral-500 uppercase tracking-wide">
                Booking number
              </p>
              <h1 className="text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
                {b.booking_number}
              </h1>
              {festivalName && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 flex items-center gap-1.5">
                  <SparkleIcon className="h-3.5 w-3.5 text-violet-500" />
                  {festivalName}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-neutral-500 uppercase tracking-wide">Total</p>
              <p className="text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
                {formatPaise(b.total_amount, currency)}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                {b.item_count} {b.item_count === 1 ? 'item' : 'items'}
              </p>
            </div>
          </div>

          {/* Customer + delivery */}
          <div className="grid md:grid-cols-2 gap-4 py-5 border-b border-neutral-100 dark:border-neutral-800">
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <UsersIcon className="h-3.5 w-3.5" />
                Customer
              </p>
              <p className="font-semibold text-neutral-900 dark:text-neutral-100">{b.customer_name}</p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="text-sm font-mono tabular-nums text-neutral-600 dark:text-neutral-300">
                  {b.customer_phone}
                </span>
                <a
                  href={`tel:${b.customer_phone}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-brand-50 text-brand-700 hover:bg-brand-100 dark:bg-brand-900/30 dark:text-brand-200 transition-colors"
                >
                  <PhoneIcon className="h-3 w-3" />
                  Call
                </a>
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-success-50 text-success-700 hover:bg-success-100 dark:bg-success-900/30 dark:text-success-200 transition-colors"
                >
                  <SendIcon className="h-3 w-3" />
                  WhatsApp
                </a>
              </div>
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <CalendarIcon className="h-3.5 w-3.5" />
                Delivery
              </p>
              <p className="font-semibold text-neutral-900 dark:text-neutral-100 font-mono tabular-nums">
                {formatBookingDate(b.delivery_date)}
              </p>
              {b.delivery_slot && (
                <p className="text-sm text-violet-600 dark:text-violet-300 mt-1">
                  {formatDeliveryTime(b.delivery_slot)}
                  {b.delivery_slot in SLOT_HOURS && (
                    <span className="text-xs text-neutral-500 font-mono tabular-nums">
                      {' '}· {SLOT_HOURS[b.delivery_slot as keyof typeof SLOT_HOURS]}
                    </span>
                  )}
                </p>
              )}
              {countdown !== null && (
                <p
                  className={cn(
                    'mt-1 text-xs font-medium',
                    overdue
                      ? 'text-danger-600 dark:text-danger-300'
                      : countdown <= 2
                        ? 'text-warning-600 dark:text-warning-300'
                        : 'text-neutral-500 dark:text-neutral-400',
                  )}
                >
                  {countdown === 0
                    ? 'Delivery today'
                    : countdown > 0
                      ? `In ${countdown} day${countdown === 1 ? '' : 's'}`
                      : `${Math.abs(countdown)} day${Math.abs(countdown) === 1 ? '' : 's'} overdue`}
                </p>
              )}
            </div>
          </div>

          {/* Totals block */}
          <div className="py-5 border-b border-neutral-100 dark:border-neutral-800 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral-500 dark:text-neutral-400">Order total</span>
              <span className="font-mono tabular-nums font-semibold text-neutral-900 dark:text-neutral-100">
                {formatPaise(b.total_amount, currency)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral-500 dark:text-neutral-400">Advance paid</span>
              <span className="font-mono tabular-nums font-semibold text-success-600">
                {formatPaise(b.advance_amount, currency)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral-500 dark:text-neutral-400">Balance due</span>
              <span
                className={cn(
                  'font-mono tabular-nums font-semibold',
                  balanceDue > 0
                    ? overdue
                      ? 'text-danger-600'
                      : 'text-warning-600'
                    : 'text-success-600',
                )}
              >
                {formatPaise(balanceDue, currency)}
              </span>
            </div>
            <div className="pt-1">
              <div className="h-2 w-full rounded-full bg-neutral-200 dark:bg-neutral-800 overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-500',
                    pct >= 100
                      ? 'bg-gradient-to-r from-success-500 to-teal-500'
                      : 'bg-gradient-to-r from-brand-500 via-indigo-500 to-violet-500',
                  )}
                  style={{ width: `${Math.max(3, pct)}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 font-mono tabular-nums text-right">
                {pct}% settled
              </p>
            </div>
          </div>

          {/* Notes */}
          {b.notes && (
            <div className="py-4 border-b border-neutral-100 dark:border-neutral-800">
              <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1.5">Notes</p>
              <p className="text-sm text-neutral-700 dark:text-neutral-200 whitespace-pre-wrap">
                {b.notes}
              </p>
            </div>
          )}

          {/* Items — lightweight since list endpoint doesn't return items */}
          <div className="pt-5">
            <p className="text-xs text-neutral-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <PackageIcon className="h-3.5 w-3.5" />
              Items
            </p>
            <div className="rounded-xl bg-neutral-50/80 dark:bg-neutral-800/50 border border-neutral-100 dark:border-neutral-700 p-4 text-sm text-neutral-600 dark:text-neutral-300">
              {b.item_count} line-item{b.item_count === 1 ? '' : 's'} on this booking. Open the festive
              event to review full line-item details and recipe breakdown.
              <div className="mt-2">
                {festivalName && (
                  <Link
                    to={`/festive-events`}
                    className="text-brand-600 dark:text-brand-300 text-xs font-medium hover:underline"
                  >
                    View festival menu →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Right: Action rail ────────────────────────────────────────── */}
        <aside className={cn(GLASS, 'p-5 animate-slide-up lg:sticky lg:top-4')}>
          <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-3">
            Actions
          </p>
          <div className="space-y-2">
            <Button
              variant="primary"
              onClick={() => setPaymentModal('BALANCE')}
              disabled={!canPayBalance}
              className="w-full justify-center"
            >
              <CurrencyIcon className="h-4 w-4 mr-1.5" />
              Pay balance{' '}
              <span className="ml-1 font-mono tabular-nums opacity-90">
                {formatPaiseCompact(balanceDue, currency)}
              </span>
            </Button>
            <Button
              variant="secondary"
              onClick={() => setPaymentModal('DEPOSIT')}
              disabled={!canDeposit}
              className="w-full justify-center"
            >
              <CheckIcon className="h-4 w-4 mr-1.5" />
              Add deposit
            </Button>
            <Button
              variant="secondary"
              onClick={() => setReminderOpen(true)}
              disabled={!canRemind || b.status === 'DELIVERED' || b.status === 'CANCELLED'}
              className="w-full justify-center"
            >
              <BellIcon className="h-4 w-4 mr-1.5" />
              Send reminder
            </Button>
            <Button
              variant="secondary"
              onClick={() => setEditOpen(true)}
              disabled={!canEdit}
              className="w-full justify-center"
            >
              <EditIcon className="h-4 w-4 mr-1.5" />
              Edit booking
            </Button>
            <Button
              variant="danger"
              onClick={() => setConfirmCancelOpen(true)}
              disabled={!canCancel}
              className="w-full justify-center"
            >
              <BanIcon className="h-4 w-4 mr-1.5" />
              Cancel booking
            </Button>
          </div>

          {/* Reminder timeline */}
          <div className="mt-5 pt-5 border-t border-neutral-100 dark:border-neutral-800">
            <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-3 flex items-center gap-1.5">
              <BellIcon className="h-3.5 w-3.5" />
              Reminder schedule
            </p>
            {reminders.length === 0 ? (
              <p className="text-xs text-neutral-500">No reminders scheduled.</p>
            ) : (
              <ul className="space-y-2">
                {reminders.map((r, i) => (
                  <li
                    key={`${r.type}-${i}`}
                    className={cn(
                      'rounded-lg px-3 py-2 text-xs',
                      r.sent
                        ? 'bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-200'
                        : 'bg-neutral-50 dark:bg-neutral-800/60 text-neutral-700 dark:text-neutral-200',
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">
                        {REMINDER_TYPE_LABEL[r.type] ?? r.type}
                      </span>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                          r.sent
                            ? 'bg-success-100 text-success-700 dark:bg-success-900/40'
                            : 'bg-warning-100 text-warning-700 dark:bg-warning-900/40',
                        )}
                      >
                        {r.sent ? 'Sent' : 'Scheduled'}
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                      {formatBookingDateTime(r.sent_at ?? r.scheduled_at)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>

      {/* Modals */}
      <PaymentModal
        open={paymentModal !== null}
        mode={paymentModal ?? 'DEPOSIT'}
        maxAmount={balanceDue}
        initialAmount={paymentModal === 'BALANCE' ? balanceDue : undefined}
        defaultMethod="UPI"
        onClose={() => setPaymentModal(null)}
        onSubmit={(amount, method) =>
          paymentModal === 'BALANCE'
            ? handleBalanceSubmit(amount, method)
            : handleDepositSubmit(amount, method)
        }
        loading={addDeposit.isPending || payBalance.isPending}
      />
      <ReminderModal
        open={reminderOpen}
        onClose={() => setReminderOpen(false)}
        onSubmit={handleReminderSubmit}
        loading={sendReminder.isPending}
      />
      <EditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        booking={b}
        onSubmit={handleEditSubmit}
        loading={false}
      />
      <ConfirmDialog
        open={confirmCancelOpen}
        title="Cancel this booking?"
        description="The cancellation charge depends on whether the festival deadline has passed. The refund amount will be shown after cancellation."
        confirmLabel="Yes, cancel booking"
        cancelLabel="Keep booking"
        variant="danger"
        onConfirm={handleCancelConfirm}
        onCancel={() => setConfirmCancelOpen(false)}
        loading={cancelBooking.isPending}
      />
      <CancelResultModal
        open={cancelResult !== null}
        onClose={() => {
          setCancelResult(null);
          navigate('/bookings');
        }}
        result={cancelResult}
      />
    </>
  );
}

export function BookingsDetailPage() {
  return (
    <PageContainer
      title="Booking Details"
      breadcrumbs={[{ label: 'Bookings', href: '/bookings' }, { label: 'Details' }]}
    >
      <ErrorBoundary>
        <BookingsDetailContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
